import React, { useState, useEffect } from 'react';
import { Card, CardBody, Badge } from 'reactstrap';
import { ApiRequest, ToastBuilder, ButtonBuilder, CommonModal } from '../../../../../../../../GlobalComponents/Builders';
import { buildSelectOptionsFromArray, parseJSONSafe } from '../../../../../../../../GlobalComponents/Helpers';
import { Trash2 } from 'react-feather';
import Select from 'react-select';

const inputStyle = { height: 34, fontSize: 13, width: '100%' };

const SetupResourcesSection = ({
    sections,
    setSections,
    sourceResources = [],
    sourceConnectionId,
    assessmentId,
    onDirtyChange,
}) => {
    const setupData = sections.setup_resources || {};
    const rows = setupData.rows || [];

    const [loadingResource, setLoadingResource] = useState(null);
    const [cachedValues, setCachedValues] = useState({});
    const [removeConfirm, setRemoveConfirm] = useState(null); // { index, row }  // resource_id -> [{id, name}]

    const setupResources = sourceResources.filter(r => parseInt(r.is_setup_resource) === 1);
    const resourceOptions = buildSelectOptionsFromArray(setupResources, { label: 'name', value: 'resource_id' });

    // Load cached values from dmt_assessment_cache for all configured rows on mount
    useEffect(() => {
        rows.forEach(row => {
            if (row.resource_id && !cachedValues[row.resource_id]) {
                loadCachedValues(row.resource_id);
            }
        });
    }, [rows.length]);

    const loadCachedValues = (resourceId) => {
        ApiRequest('GET', '/dmt/assessments/{assessmentId}/cache', {
            pathParams: { assessmentId },
            queryParams: { conditions: `resource_id = ${resourceId}` },
        }).then(res => {
            const row = (Array.isArray(res.data) ? res.data : [])[0];
            if (row?.cached_ids) {
                const values = typeof row.cached_ids === 'string' ? parseJSONSafe(row.cached_ids) : row.cached_ids;
                if (Array.isArray(values)) {
                    setCachedValues(prev => ({ ...prev, [resourceId]: values }));
                }
            }
        });
    };

    const updateRows = (newRows) => {
        setSections(prev => ({
            ...prev,
            setup_resources: { ...(prev.setup_resources || {}), rows: newRows },
        }));
        if (onDirtyChange) onDirtyChange(true);
    };

    const addRow = () => {
        updateRows([...rows, { resource_id: '', resource_name: '', resource_uri: '', resource_options: null }]);
    };

    const updateRow = (index, updates) => {
        const newRows = [...rows];
        newRows[index] = { ...newRows[index], ...updates };
        updateRows(newRows);
    };

    const removeRow = (index) => {
        const row = rows[index];
        const hasCached = row.resource_id && (cachedValues[row.resource_id] || []).length > 0;

        if (hasCached) {
            setRemoveConfirm({ index, row });
        } else {
            updateRows(rows.filter((_, i) => i !== index));
        }
    };

    const confirmRemove = (alsoDeleteCache) => {
        if (!removeConfirm) return;
        const { index, row } = removeConfirm;

        if (alsoDeleteCache) {
            ApiRequest('POST', '/dmt/assessments/{assessmentId}/setupValues', {
                pathParams: { assessmentId },
                body: { connectionId: sourceConnectionId, resourceId: row.resource_id, resourceUri: row.resource_uri || '', resourceOptions: {}, _clear: true },
            });
            setCachedValues(prev => {
                const next = { ...prev };
                delete next[row.resource_id];
                return next;
            });
        }

        const newRows = rows.filter((_, i) => i !== index);
        updateRows(newRows);
        setRemoveConfirm(null);

        // Auto-save so the removal persists without manual save
        ApiRequest('POST', '/dmt/assessments/{assessmentId}/sections', {
            pathParams: { assessmentId },
            body: { section_key: 'setup_resources', section_data: { rows: newRows } },
        });
        if (onDirtyChange) onDirtyChange(false);
    };

    const handleResourceSelect = (index, resourceId) => {
        const resource = setupResources.find(r => String(r.resource_id) === String(resourceId));
        if (!resource) {
            updateRow(index, { resource_id: '', resource_name: '', resource_uri: '', resource_options: null });
            return;
        }
        const opts = typeof resource.resource_options === 'string' ? parseJSONSafe(resource.resource_options) : resource.resource_options;
        updateRow(index, {
            resource_id: resource.resource_id,
            resource_name: resource.name,
            resource_uri: resource.resource,
            resource_options: opts,
        });
        // Load cached values if they exist
        if (!cachedValues[resource.resource_id]) {
            loadCachedValues(resource.resource_id);
        }
    };

    const fetchValues = (index) => {
        const row = rows[index];
        if (!row.resource_id || !sourceConnectionId) return;

        setLoadingResource(index);
        ApiRequest('POST', '/dmt/assessments/{assessmentId}/setupValues', {
            pathParams: { assessmentId },
            body: { connectionId: sourceConnectionId, resourceId: row.resource_id, resourceUri: row.resource_uri, resourceOptions: row.resource_options || {} },
        }).then(res => {
            setLoadingResource(null);
            if (res.success && res.data?.values) {
                setCachedValues(prev => ({ ...prev, [row.resource_id]: res.data.values }));
                ToastBuilder('success', `${row.resource_name}: ${res.data.count} values cached`);
            } else {
                ToastBuilder('error', res.error || 'Failed to fetch values');
            }
        });
    };

    if (!sourceConnectionId) {
        return <p className='text-muted'>Configure a source connection in Project Overview to load setup resources.</p>;
    }

    if (setupResources.length === 0) {
        return <p className='text-muted'>No setup resources found for the source system.</p>;
    }

    return (
        <div className='dmt-setup-resources'>
            <div className='mb-3'>
                <ButtonBuilder label='Add Setup Resource' className='btn-outline-primary btn-sm' onClick={addRow} />
            </div>

            {rows.map((row, idx) => {
                const values = cachedValues[row.resource_id] || [];
                const hasCached = values.length > 0;

                return (
                    <Card key={idx} className='mb-3' style={{ borderLeft: `3px solid ${hasCached ? '#28a745' : '#6c757d'}` }}>
                        <CardBody>
                            <div className='d-flex gap-3'>
                                {/* Left: resource select + actions */}
                                <div style={{ flex: '0 0 280px' }}>
                                    <Select
                                        options={resourceOptions}
                                        value={resourceOptions.find(o => String(o.value) === String(row.resource_id)) || null}
                                        onChange={(opt) => handleResourceSelect(idx, opt?.value || '')}
                                        placeholder='Select setup resource...'
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={{
                                            control: (base) => ({ ...base, minHeight: 34, height: 34, fontSize: 13 }),
                                            valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                                            input: (base) => ({ ...base, margin: 0, padding: 0 }),
                                            indicatorsContainer: (base) => ({ ...base, height: 32 }),
                                            indicatorSeparator: () => ({ display: 'none' }),
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            option: (base) => ({ ...base, fontSize: 13, padding: '6px 10px' }),
                                        }}
                                    />
                                    <div className='d-flex align-items-center gap-2 mt-2'>
                                        {row.resource_id && (
                                            <ButtonBuilder
                                                label={hasCached ? 'Refresh' : 'Fetch Values'}
                                                className={hasCached ? 'btn-outline-success btn-sm' : 'btn-primary btn-sm'}
                                                onClick={() => fetchValues(idx)}
                                                useSpinner={loadingResource === idx}
                                                disabled={loadingResource !== null}
                                            />
                                        )}
                                        {hasCached && <Badge color='success'>{values.length} values</Badge>}
                                        <Trash2 size={15} style={{ cursor: 'pointer', color: '#dc3545', marginLeft: 'auto' }} onClick={() => removeRow(idx)} />
                                    </div>
                                </div>

                                {/* Right: cached values */}
                                {hasCached && (
                                    <div style={{ flex: '1 1 auto', maxHeight: 160, overflow: 'auto', fontSize: 12, padding: 8, borderRadius: 4, border: '1px solid rgba(128,128,128,0.2)' }}>
                                        {values.map((v, vi) => (
                                            <div key={vi} className='d-flex gap-2' style={{ padding: '2px 0', borderBottom: vi < values.length - 1 ? '1px solid rgba(128,128,128,0.1)' : 'none' }}>
                                                <span className='text-muted' style={{ minWidth: 50, fontFamily: 'monospace', fontSize: 11 }}>{v.id}</span>
                                                <span>{v.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                );
                })}

            {rows.length === 0 && (
                <p className='text-muted'>Add setup resources to cache their values for use in record count filters.</p>
            )}

            {/* Remove confirmation modal */}
            <CommonModal
                isOpen={!!removeConfirm}
                toggler={() => setRemoveConfirm(null)}
                title={`Remove ${removeConfirm?.row?.resource_name || 'Setup Resource'}`}
                onSubmit={false}
            >
                <p>Remove <strong>{removeConfirm?.row?.resource_name}</strong> from setup resources?</p>
                <p className='text-muted' style={{ fontSize: 13 }}>This resource has cached values in the database. Would you also like to delete them?</p>
                <div className='d-flex gap-2 justify-content-end mt-3'>
                    <button className='btn btn-sm btn-outline-secondary' onClick={() => { confirmRemove(false); }}>Remove Only</button>
                    <button className='btn btn-sm btn-danger' onClick={() => { confirmRemove(true); }}>Remove & Delete Cache</button>
                </div>
            </CommonModal>
        </div>
    );
};

export default SetupResourcesSection;
